import { useEffect, useRef } from "react";
import { findShortcut, SCOPES } from "./keyboardShortcutRegistry";
import isTypingInFormOverlay from "../../../../utils/isTypingInFormOverlay";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { trace } from "../../../../utils/debugTrace";

const usePOSKeyboard = ({
  posConfig,
  cart,
  tillLayout,
  // Modal/drawer togglers
  setSalesRepModalOpen,
  setSalesRepresentSearchInput,
  salesReprestiveList,
  setFilteredSalesRepresentList,
  setSelectedRowKeys,
  openPaymentModal,
  setDisplayOrderHistory,
  setDisplayParkedBillModal,
  setIsQtyUpdate,
  parkBill,
  confirmDiscardCart,
  closeProductPanel,
  setDisplayBatchSelection,
  closeCustomerSearch,
  closePaymentModal,
  // Cart operations
  increaseProductQty,
  decreaseProductQty,
  deleteProduct,
  // State refs (kept in sync via useEffect)
  selectedProductInCart,
  setSelectedProductInCart,
  selectedRowKeys,
  paymentModal,
  selectedItemsModalOpen,
  // Setter for payment method on paymentModal open
  setSelectedPaymentMethod,
  tillDataPaymentMethods,
  // SuperMarket/Retail (tillLayout !== 2) navigates to payment via the cart
  // total click flow, not the Restaurant paymentModal toggle.
  handleCartTotalClick,
  // Zone navigation (optional — Phase 1 keyboard nav)
  zoneNavigation,
  layoutType,
  newShortcutHandlers,
  // True when any customer overlay (search / add / edit / UAE) is open. Cart
  // shortcuts must be suppressed while it is, regardless of where focus is — the
  // customer search box reuses the product-search id, and focus often stays on
  // the product search behind the drawer, so a focus check alone is not enough.
  customerOverlayOpen,
}) => {
  // ── Internal refs to track latest values ────────────────────────────
  const cartItemsLengthRef = useRef(0);
  const selectedProductInCartRef = useRef({});
  const selectedRowKeysRef = useRef([0]);
  const paymentModalStateRef = useRef(false);

  useEffect(() => {
    cartItemsLengthRef.current = cart.items.length;
  }, [cart]);

  useEffect(() => {
    selectedProductInCartRef.current = selectedProductInCart;
  }, [selectedProductInCart]);

  useEffect(() => {
    selectedRowKeysRef.current = selectedRowKeys;
    // A line without a `key` (e.g. retrieved from order history before any recalc)
    // yields [undefined]; findIndex(ci.key === undefined) would match the first
    // unkeyed line and overwrite the row the cashier actually clicked.
    if (selectedRowKeys.length > 0 && selectedRowKeys[0] != null) {
      // BUG-1926: cart.js assigns `key` as a plain positional index (0, 1, 2…)
      // independently within each cart's items array, so the return cart's
      // first line and the exchange cart's first line both end up with key=0.
      // This effect used to search only cart.items (the return cart), so
      // clicking an exchange-cart row set selectedProductInCart correctly via
      // onRowClick, then this effect immediately fired on the same
      // selectedRowKeys change, matched the return line by coincidental key
      // collision, and silently overwrote the selection with the return
      // line's data (negative weight, isReturn qty-edit lock). When the
      // pre-existing selection is a non-return item in EXCHANGE mode, resolve
      // the key against exchangeCart.items instead.
      const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
      if (isExchangeMode && selectedProductInCartRef.current && !selectedProductInCartRef.current.isReturn) {
        const exchangeItems = useCartStore.getState().exchangeCart?.items || [];
        const exchangeIndex = exchangeItems.findIndex((ci) => ci.key === selectedRowKeys[0]);
        if (exchangeIndex !== -1) {
          // This effect depends on `cart`, not just `selectedRowKeys`, so it re-fires on
          // every cart recalculation (pricing rules, key reassignment, etc.) -- not only
          // on genuine row navigation. Unconditionally overwriting selectedProductInCart
          // here clobbered a cashier's in-progress qty edit with the store's
          // last-*committed* weight several times a second, mid-keystroke, since the new
          // value they're typing hasn't been committed via Enter yet. Only resync when
          // the resolved key now points at a genuinely different line than what's already
          // selected -- e.g. arrow-key navigation -- so an in-progress edit on the same
          // line is left alone.
          const isSameLine = selectedProductInCartRef.current?.lineId === exchangeItems[exchangeIndex]?.lineId;
          if (!isSameLine) {
            trace("SMSelection", "usePOSKeyboard: resolved key against exchangeCart", {
              key: selectedRowKeys[0],
              productId: exchangeItems[exchangeIndex]?.productId,
              weight: exchangeItems[exchangeIndex]?.weight,
            });
            setSelectedProductInCart({ ...exchangeItems[exchangeIndex] });
          }
          return;
        }
      }
      const cartIndex = cart.items.findIndex((ci) => ci.key === selectedRowKeys[0]);
      // Keyboard Up/Down only ever calls setSelectedRowKeys — without this sync,
      // selectedProductInCartRef stays pinned to whatever was last clicked, so
      // Delete/+/- silently act on the wrong (or no) row after arrow-key navigation.
      if (cartIndex !== -1) {
        if (isExchangeMode) {
          trace("SMSelection", "usePOSKeyboard: resolved key against return cart.items", {
            key: selectedRowKeys[0],
            productId: cart.items[cartIndex]?.productId,
            weight: cart.items[cartIndex]?.weight,
            isReturn: cart.items[cartIndex]?.isReturn,
          });
        }
        setSelectedProductInCart({ ...cart.items[cartIndex] });
      }
    }
  }, [selectedRowKeys, cart]);

  useEffect(() => {
    paymentModalStateRef.current = paymentModal;
    if (paymentModal) {
      setSelectedPaymentMethod(tillDataPaymentMethods.filter((item, index) => item.isDefault === "Y")?.[0] || {});
    }
  }, [paymentModal]);

  // ── handleKey ─────────────────────────────────────────────────────────

  const handleKey = (e) => {
    const { altKey, keyCode } = e;
    const featureEnabled = posConfig?.enableKeyboardNavigation === "Y";
    // Suppress cart-interaction shortcuts while any customer overlay is open
    // (search / add / edit / UAE), or focus is inside some other form overlay.
    // Main-screen keys (incl. the quantity field when a cart item is selected)
    // keep working.
    const typingInField = customerOverlayOpen || isTypingInFormOverlay(e.target);

    // BUG-1756: "+"/"-" are printable characters a cashier can legitimately type
    // as part of a product code (e.g. "TW-1495"). The product search input is
    // exempt from typingInField above so scanning/quick qty-adjust keeps working
    // while it's focused (its normal idle state), but once it already has text
    // in progress a keystroke there is product-code entry, not a request to
    // bump the selected cart row's quantity.
    const composingProductSearch = e.target?.id === "sm-product-search" && typeof e.target?.value === "string" && e.target.value.length > 0;

    if (featureEnabled && zoneNavigation) {
      const isFKey = keyCode >= 112 && keyCode <= 119;
      if (isFKey) {
        const handled = zoneNavigation.handleFKeyPress(keyCode, e);
        if (handled) return;
      }
      if (zoneNavigation.activeZoneId && zoneNavigation.handleInnerNavigation(e)) {
        return;
      }
    }

    if (altKey) {
      if (keyCode === 83) {
        if (posConfig?.showLineSalesRep === "Y") {
          setSalesRepModalOpen({ status: true, title: "itemSalesRep" });
          setSalesRepresentSearchInput("");
          setFilteredSalesRepresentList(salesReprestiveList);
        }
      }
      if (keyCode === 67) {
        setSelectedRowKeys((v) => {
          if (v.length > 0) {
            return [];
          } else {
            return [0];
          }
        });
      }
      if (keyCode === 80) {
        // openPaymentModal only flips the Restaurant modal flag (tillLayout === 2);
        // SuperMarket/Retail opens the payment view via the cart-total click flow.
        // TIC-9341: cartItemsLengthRef is synced by its own separate effect and can
        // lag behind the live cart (e.g. right after a product-detail/BOM add), so
        // Alt+P silently no-op'd even with items in cart while the on-screen
        // total's click handler -- which calls handleCartTotalClick with no such
        // pre-check -- worked fine. handleCartTotalClick already re-reads the cart
        // fresh from the store and only opens the payment view when it has items,
        // so drop the redundant, staleness-prone ref gate and let it decide.
        if (tillLayout === 2) {
          openPaymentModal();
        } else {
          handleCartTotalClick?.();
        }
      }

      if (keyCode === 79) {
        setDisplayOrderHistory(true);
      }

      if (keyCode === 66) {
        setDisplayParkedBillModal(true);
      }

      if (keyCode === 73) {
        if (selectedRowKeysRef.current.length > 0) {
          setIsQtyUpdate(selectedProductInCartRef.current);
        }
      }

      if (keyCode === 76) {
        parkBill("parkKey");
      }

      if (keyCode === 72) {
        confirmDiscardCart();
      }
    }

    if (keyCode === 115) {
      // F4 — handled by zone navigation when enableKeyboardNavigation === "Y"
    }
    if (keyCode === 27) {
      if ((e.target.id === "productSearchInputId" || e.target.id.search("productCardItem")) >= 0) {
        closeProductPanel();
      }
      setDisplayBatchSelection((b) => {
        if (b) {
          return !b;
        } else {
          return b;
        }
      });
      setSalesRepModalOpen({ status: false, title: "" });
      setSalesRepresentSearchInput("");
      closeCustomerSearch();

      if (paymentModalStateRef.current) {
        closePaymentModal();
      }
    }
    if (keyCode === 38 && !typingInField) {
      setSelectedRowKeys((v) => {
        if (v.length > 0 && v[0] > 0) {
          return [v[0] - 1];
        } else {
          return [v[0]];
        }
      });
    }
    if (keyCode === 40 && !typingInField) {
      setSelectedRowKeys((v) => {
        if (v.length > 0 && v[0] < cartItemsLengthRef.current - 1) {
          return [v[0] + 1];
        } else {
          return [v[0]];
        }
      });
    }

    if (keyCode === 187 || keyCode === 107) {
      if (!typingInField && !composingProductSearch && selectedRowKeysRef?.current?.length > 0 && !selectedItemsModalOpen && cart.saleType?.value !== "DI") {
        increaseProductQty(selectedProductInCartRef.current);
      }
    }
    if (keyCode === 109 || keyCode === 189) {
      if (!typingInField && !composingProductSearch && selectedRowKeysRef?.current?.length > 0 && !selectedItemsModalOpen && cart.saleType?.value !== "DI") {
        if (parseFloat(selectedProductInCartRef?.current?.weight) > 1) {
          decreaseProductQty(selectedProductInCartRef.current);
        }
      }
    }
    if (keyCode === 46 && !typingInField) {
      if (selectedRowKeysRef?.current?.length > 0) {
        deleteProduct(selectedProductInCartRef.current);
      }
    }

    if (featureEnabled && keyCode === 27 && zoneNavigation?.activeZoneId) {
      zoneNavigation.clearZoneFocus();
    }

    if (featureEnabled && newShortcutHandlers) {
      const scope = layoutType === "restaurant" ? SCOPES.RESTAURANT : SCOPES.SUPERMARKET;
      const shortcut = findShortcut(e, scope, true);
      if (shortcut?.gated) {
        const handler = newShortcutHandlers[shortcut.action];
        if (handler) {
          e.preventDefault();
          handler(e, shortcut);
        }
      }
    }
  };

  // ── Wire up global keydown listener ───────────────────────────────────
  const handleKeyRef = useRef(handleKey);
  handleKeyRef.current = handleKey;

  useEffect(() => {
    const listener = (e) => handleKeyRef.current(e);
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  // ── Return ────────────────────────────────────────────────────────────
  return {
    cartItemsLengthRef,
    selectedProductInCartRef,
    selectedRowKeysRef,
    paymentModalStateRef,
    handleKey,
  };
};

export default usePOSKeyboard;
